import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichText } from "@/components/cms/RichText";
import { MediaPicker } from "@/components/cms/MediaPicker";
import {
  LOGO_SIZE_OPTS, HERO_HEIGHT_OPTS, BUFFER_OPTS, FOCAL_Y_OPTS, FOCAL_X_OPTS,
  VIDEO_TIMING_OPTS, VIDEO_CATEGORY_OPTS,
} from "@/lib/heroPresets";
import {
  Loader2, Save, RotateCcw, Globe, ExternalLink, Plus, Trash2,
  ChevronUp, ChevronDown, Image as ImageIcon, Video, Eye, EyeOff, Images,
} from "lucide-react";

const SECTION_LABELS = {
  persona: "Persona Panel", services: "Service Lanes", process: "1-2-3 Process",
  trust: "Trust Section", trilogy: "Trilogy Wellness", service_areas: "Service Area Search",
  resources: "Resources / Q&A", testimonials: "Testimonials", final_cta: "Final CTA",
};
const DEFAULT_SECTION_ORDER = [
  "persona", "services", "process", "trust", "trilogy",
  "service_areas", "resources", "testimonials", "final_cta",
];
const OVERLAY_OPTIONS = [
  { v: "side", label: "Side gradient (default)" },
  { v: "bottom", label: "Bottom gradient" },
  { v: "dark", label: "Dark overlay" },
  { v: "none", label: "Light overlay" },
];
const slideId = () => `s_${Math.random().toString(36).slice(2, 9)}`;

const NAVY = "#1C4358";

// deep-set an immutable copy at a dot-path
const setPath = (obj, path, value) => {
  const keys = path.split(".");
  const next = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return next;
};

const getPath = (obj, path) =>
  path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);

const Field = ({ label, path, content, onChange, textarea, rows = 3, placeholder }) => {
  const val = getPath(content, path) ?? "";
  const id = `f-${path.replace(/\./g, "-")}`;
  const cls = "text-[15px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0";
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[13px] font-semibold text-[#1C4358]">{label}</Label>
      {textarea ? (
        <Textarea id={id} data-testid={`editor-${path}`} rows={rows} className={cls}
          value={val} placeholder={placeholder} onChange={(e) => onChange(path, e.target.value)} />
      ) : (
        <Input id={id} data-testid={`editor-${path}`} className={cls}
          value={val} placeholder={placeholder} onChange={(e) => onChange(path, e.target.value)} />
      )}
    </div>
  );
};

const RichField = ({ label, path, content, onChange }) => {
  const val = getPath(content, path) ?? "";
  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-semibold text-[#1C4358]">{label}</Label>
      <RichText testid={`editor-${path}`} value={val} onChange={(html) => onChange(path, html)} />
    </div>
  );
};

const MediaField = ({ label, urlPath, altPath, content, onChange, testid, help }) => (
  <MediaPicker
    label={label}
    testid={testid}
    help={help}
    value={getPath(content, urlPath) ?? ""}
    alt={getPath(content, altPath) ?? ""}
    onUrlChange={(url) => onChange(urlPath, url)}
    onAltChange={(alt) => onChange(altPath, alt)}
  />
);

const SelectField = ({ label, path, content, onChange, options, placeholder, help, fallback, testid }) => {
  const raw = getPath(content, path);
  const val = raw == null || raw === "" ? (fallback ?? "") : String(raw);
  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-semibold text-[#1C4358]">{label}</Label>
      <Select value={val} onValueChange={(v) => onChange(path, v)}>
        <SelectTrigger data-testid={testid || `editor-${path}`} className="h-10 text-[14px]"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
      {help ? <p className="text-[12px] text-[#6B7280]">{help}</p> : null}
    </div>
  );
};

// Logo size control: presets + optional custom px.
const SizeField = ({ label, path, content, onChange, help, customDefault = 180 }) => {
  const raw = getPath(content, path);
  const isNum = typeof raw === "number" || (typeof raw === "string" && /^\d+$/.test(raw));
  const selectVal = isNum ? "custom" : (raw || "xlarge");
  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-semibold text-[#1C4358]">{label}</Label>
      <div className="flex gap-2">
        <Select value={selectVal} onValueChange={(v) => onChange(path, v === "custom" ? customDefault : v)}>
          <SelectTrigger data-testid={`editor-${path}`} className="h-10 text-[14px]"><SelectValue /></SelectTrigger>
          <SelectContent>{LOGO_SIZE_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        {selectVal === "custom" && (
          <Input type="number" min={24} max={400} data-testid={`editor-${path}-custom`} className="h-10 w-28 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0"
            value={isNum ? raw : customDefault} onChange={(e) => onChange(path, parseInt(e.target.value || "0", 10) || 0)} />
        )}
      </div>
      {help ? <p className="text-[12px] text-[#6B7280]">{help}</p> : null}
    </div>
  );
};

const vidId = () => `v_${Math.random().toString(36).slice(2, 9)}`;

const VideoLibraryEditor = ({ content, onChange }) => {
  const videos = content?.videos || [];
  const setVideos = (next) => onChange("videos", next);
  const update = (i, patch) => setVideos(videos.map((v, vi) => (vi === i ? { ...v, ...patch } : v)));
  const add = () => setVideos([...videos, { id: vidId(), title: "", description: "", video_url: "", poster_url: "", category: "welcome", enabled: true, featured: false, order: videos.length }]);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= videos.length) return;
    const n = [...videos];
    [n[i], n[j]] = [n[j], n[i]];
    setVideos(n.map((v, idx) => ({ ...v, order: idx })));
  };
  const del = (i) => setVideos(videos.filter((_, vi) => vi !== i));
  return (
    <div className="space-y-3" data-testid="video-library-editor">
      {videos.map((v, i) => (
        <div key={v.id || i} className="rounded-lg border border-[#E6EDF2] bg-white p-3 space-y-3" data-testid={`video-row-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] font-bold text-[#1C4358]">Video {i + 1}</span>
            <div className="flex items-center gap-1">
              <label className="flex items-center gap-1.5 text-[12px] text-[#4B5563] mr-2">
                <Switch data-testid={`video-${i}-enabled`} checked={v.enabled !== false} onCheckedChange={(val) => update(i, { enabled: val })} /> Visible
              </label>
              <button type="button" data-testid={`video-${i}-up`} disabled={i === 0} onClick={() => move(i, -1)} className="h-7 w-7 inline-flex items-center justify-center rounded text-[#1C4358] hover:bg-[#1C4358]/10 disabled:opacity-30"><ChevronUp size={15} /></button>
              <button type="button" data-testid={`video-${i}-down`} disabled={i === videos.length - 1} onClick={() => move(i, 1)} className="h-7 w-7 inline-flex items-center justify-center rounded text-[#1C4358] hover:bg-[#1C4358]/10 disabled:opacity-30"><ChevronDown size={15} /></button>
              <button type="button" data-testid={`video-${i}-delete`} onClick={() => del(i)} className="h-7 w-7 inline-flex items-center justify-center rounded text-[#9C1E22] hover:bg-[#9C1E22]/10"><Trash2 size={15} /></button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Title</Label>
            <Input data-testid={`video-${i}-title`} value={v.title || ""} className="h-9 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" onChange={(e) => update(i, { title: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Description</Label>
            <Textarea data-testid={`video-${i}-desc`} rows={2} value={v.description || ""} className="text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" onChange={(e) => update(i, { description: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Video URL (YouTube, Vimeo, or .mp4)</Label>
            <Input data-testid={`video-${i}-url`} value={v.video_url || ""} placeholder="https://youtube.com/watch?v=…" className="h-9 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" onChange={(e) => update(i, { video_url: e.target.value })} />
          </div>
          <MediaPicker label="Poster image" testid={`video-${i}-poster`} value={v.poster_url || ""} alt={v.poster_alt || ""}
            onUrlChange={(url) => update(i, { poster_url: url })} onAltChange={(alt) => update(i, { poster_alt: alt })} />
          <div className="grid sm:grid-cols-2 gap-2 items-end">
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-[#1C4358]">Category</Label>
              <Select value={v.category || "welcome"} onValueChange={(val) => update(i, { category: val })}>
                <SelectTrigger data-testid={`video-${i}-category`} className="h-9 text-[14px]"><SelectValue /></SelectTrigger>
                <SelectContent>{VIDEO_CATEGORY_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-[12px] text-[#4B5563] h-9">
              <Switch data-testid={`video-${i}-featured`} checked={!!v.featured} onCheckedChange={(val) => update(i, { featured: val })} /> Featured
            </label>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" data-testid="video-add" onClick={add} className="gap-1.5 h-8 text-[13px]"><Plus size={14} /> Add video</Button>
    </div>
  );
};

const HeroSlidesEditor = ({ content, onChange }) => {
  const slides = content?.hero?.slides || [];
  const setSlides = (next) => onChange("hero.slides", next);
  const update = (i, patch) => setSlides(slides.map((s, si) => (si === i ? { ...s, ...patch } : s)));
  const addSlide = (type) =>
    setSlides([...slides, { id: slideId(), type, enabled: true, order: slides.length, overlay: "side", show_call: true, show_chat: true }]);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= slides.length) return;
    const n = [...slides];
    [n[i], n[j]] = [n[j], n[i]];
    setSlides(n.map((s, idx) => ({ ...s, order: idx })));
  };
  const del = (i) => setSlides(slides.filter((_, si) => si !== i));

  return (
    <div className="rounded-xl border border-[#E6EDF2] bg-[#F7FAFC] p-4 space-y-3" data-testid="hero-slides-editor">
      <div>
        <p className="text-[14px] font-display font-bold text-[#1C4358] flex items-center gap-1.5"><Images size={16} /> Hero carousel slides</p>
        <p className="text-[12px] text-[#6B7280]">Add 2+ slides to turn the hero into a carousel. With none, the single background image above is used. Video plays in a popup (no autoplay sound).</p>
      </div>
      {slides.map((s, i) => (
        <div key={s.id || i} className="rounded-lg border border-[#E6EDF2] bg-white p-3 space-y-3" data-testid={`hero-slide-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-bold text-[#1C4358]">Slide {i + 1}</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#1C4358]/8 text-[#1C4358] inline-flex items-center gap-1">
                {s.type === "video" ? <><Video size={11} /> Video</> : <><ImageIcon size={11} /> Image</>}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <label className="flex items-center gap-1.5 text-[12px] text-[#4B5563] mr-2">
                <Switch data-testid={`hero-slide-${i}-enabled`} checked={s.enabled !== false} onCheckedChange={(v) => update(i, { enabled: v })} /> Visible
              </label>
              <button type="button" data-testid={`hero-slide-${i}-up`} disabled={i === 0} onClick={() => move(i, -1)} className="h-7 w-7 inline-flex items-center justify-center rounded text-[#1C4358] hover:bg-[#1C4358]/10 disabled:opacity-30"><ChevronUp size={15} /></button>
              <button type="button" data-testid={`hero-slide-${i}-down`} disabled={i === slides.length - 1} onClick={() => move(i, 1)} className="h-7 w-7 inline-flex items-center justify-center rounded text-[#1C4358] hover:bg-[#1C4358]/10 disabled:opacity-30"><ChevronDown size={15} /></button>
              <button type="button" data-testid={`hero-slide-${i}-delete`} onClick={() => del(i)} className="h-7 w-7 inline-flex items-center justify-center rounded text-[#9C1E22] hover:bg-[#9C1E22]/10"><Trash2 size={15} /></button>
            </div>
          </div>
          <MediaPicker
            label={s.type === "video" ? "Poster image (shown before play)" : "Slide image"}
            testid={`hero-slide-${i}-media`}
            value={s.type === "video" ? (s.poster_url || "") : (s.image_url || "")}
            alt={s.alt || ""}
            onUrlChange={(url) => update(i, s.type === "video" ? { poster_url: url } : { image_url: url })}
            onAltChange={(alt) => update(i, { alt })}
          />
          {s.type === "video" && (
            <div className="space-y-1.5">
              <Label className="text-[13px] font-semibold text-[#1C4358]">Video URL (YouTube, Vimeo, or .mp4)</Label>
              <Input data-testid={`hero-slide-${i}-video`} value={s.video_url || ""} placeholder="https://youtube.com/watch?v=…"
                className="text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" onChange={(e) => update(i, { video_url: e.target.value })} />
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-[#1C4358]">Eyebrow</Label>
              <Input data-testid={`hero-slide-${i}-eyebrow`} value={s.eyebrow || ""} className="h-9 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" onChange={(e) => update(i, { eyebrow: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-[#1C4358]">Overlay</Label>
              <Select value={s.overlay || "side"} onValueChange={(v) => update(i, { overlay: v })}>
                <SelectTrigger data-testid={`hero-slide-${i}-overlay`} className="h-9 text-[14px]"><SelectValue /></SelectTrigger>
                <SelectContent>{OVERLAY_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Headline</Label>
            <Textarea data-testid={`hero-slide-${i}-headline`} rows={2} value={s.headline || ""} placeholder="Leave blank to use the main hero headline" className="text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" onChange={(e) => update(i, { headline: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Subheadline</Label>
            <Textarea data-testid={`hero-slide-${i}-subheadline`} rows={2} value={s.subheadline || ""} className="text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" onChange={(e) => update(i, { subheadline: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-[#1C4358]">CTA label</Label>
              <Input data-testid={`hero-slide-${i}-cta-label`} value={s.cta_label || ""} placeholder="Book a Free Consultation" className="h-9 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" onChange={(e) => update(i, { cta_label: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-[#1C4358]">CTA link</Label>
              <Input data-testid={`hero-slide-${i}-cta-url`} value={s.cta_url || ""} placeholder="/book" className="h-9 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" onChange={(e) => update(i, { cta_url: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-[12px] text-[#4B5563]"><Switch checked={s.show_call !== false} onCheckedChange={(v) => update(i, { show_call: v })} /> Show “Call” button</label>
            <label className="flex items-center gap-2 text-[12px] text-[#4B5563]"><Switch checked={s.show_chat !== false} onCheckedChange={(v) => update(i, { show_chat: v })} /> Show “Chat” button</label>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" data-testid="hero-add-image-slide" onClick={() => addSlide("image")} className="gap-1.5 h-8 text-[13px]"><Plus size={14} /> Image slide</Button>
        <Button type="button" size="sm" variant="outline" data-testid="hero-add-video-slide" onClick={() => addSlide("video")} className="gap-1.5 h-8 text-[13px]"><Plus size={14} /> Video slide</Button>
      </div>
    </div>
  );
};

const SectionsEditor = ({ content, onChange }) => {
  const vis = content?.section_visibility || {};
  const stored = Array.isArray(content?.section_order) ? content.section_order.filter((k) => SECTION_LABELS[k]) : [];
  const order = [...stored, ...DEFAULT_SECTION_ORDER.filter((k) => !stored.includes(k))];
  const setOrder = (next) => onChange("section_order", next);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const n = [...order];
    [n[i], n[j]] = [n[j], n[i]];
    setOrder(n);
  };
  return (
    <div className="space-y-2" data-testid="sections-editor">
      <p className="text-[12px] text-[#6B7280]">The Hero is always first. Reorder or hide any homepage section below.</p>
      {order.map((k, i) => (
        <div key={k} className="flex items-center justify-between rounded-lg border border-[#E6EDF2] bg-white px-3 py-2.5" data-testid={`section-row-${k}`}>
          <div className="flex items-center gap-2">
            <div className="flex flex-col">
              <button type="button" data-testid={`section-${k}-up`} disabled={i === 0} onClick={() => move(i, -1)} className="h-5 w-5 inline-flex items-center justify-center rounded text-[#1C4358] hover:bg-[#1C4358]/10 disabled:opacity-30"><ChevronUp size={14} /></button>
              <button type="button" data-testid={`section-${k}-down`} disabled={i === order.length - 1} onClick={() => move(i, 1)} className="h-5 w-5 inline-flex items-center justify-center rounded text-[#1C4358] hover:bg-[#1C4358]/10 disabled:opacity-30"><ChevronDown size={14} /></button>
            </div>
            <span className="text-[14px] font-medium text-[#252221]">{SECTION_LABELS[k]}</span>
          </div>
          <label className="flex items-center gap-2 text-[12px] text-[#4B5563]">
            {vis[k] === false ? <EyeOff size={14} className="text-[#9C1E22]" /> : <Eye size={14} className="text-[#2F7D5A]" />}
            <Switch data-testid={`section-${k}-toggle`} checked={vis[k] !== false} onCheckedChange={(v) => onChange(`section_visibility.${k}`, v)} />
          </label>
        </div>
      ))}
    </div>
  );
};

export default function SiteEditor() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site/homepage");
      setContent(data.content);
      setUpdatedAt(data.updated_at);
    } catch (e) {
      toast.error("Could not load homepage content.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onChange = (path, value) => setContent((c) => setPath(c, path, value));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/site/homepage", { content });
      setContent(data.content);
      toast.success("Homepage saved. Live site updated.");
      setUpdatedAt(new Date().toISOString());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin text-[#1C4358]" size={30} />
      </div>
    );
  }
  if (!content) return null;

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="site-editor">
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 bg-white/95 backdrop-blur border-b border-[#E6EDF2] flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Globe size={20} style={{ color: NAVY }} />
            <h1 className="text-xl font-display font-bold text-[#1C4358]">Homepage Editor</h1>
          </div>
          <p className="text-[13px] text-[#4B5563]">Global content — edits go live on the public homepage.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1C4358] px-3 h-10 rounded-lg hover:bg-[#1C4358]/6">
            View site <ExternalLink size={15} />
          </a>
          <Button variant="outline" onClick={load} data-testid="editor-reset" className="gap-1.5">
            <RotateCcw size={15} /> Revert
          </Button>
          <Button onClick={save} disabled={saving} data-testid="editor-save"
            className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save changes
          </Button>
        </div>
      </div>

      {updatedAt && (
        <p className="mt-4 text-[13px] text-[#4B5563]">Last updated {new Date(updatedAt).toLocaleString()}</p>
      )}

      <Card className="mt-4 p-5 border-[#E6EDF2]">
        <Field label="Business phone number" path="phone" content={content} onChange={onChange} placeholder="816-642-8501" />
      </Card>

      <Accordion type="multiple" defaultValue={["hero"]} className="mt-4 space-y-3">
        <AccordionItem value="hero" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Hero</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Eyebrow" path="hero.eyebrow" content={content} onChange={onChange} />
            <Field label="Headline" path="hero.headline" content={content} onChange={onChange} textarea rows={2} />
            <Field label="Subheadline" path="hero.subheadline" content={content} onChange={onChange} textarea />
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Primary CTA label" path="hero.cta_primary.label" content={content} onChange={onChange} />
              <Field label="Call CTA label" path="hero.cta_call.label" content={content} onChange={onChange} />
              <Field label="Chat CTA label" path="hero.cta_chat.label" content={content} onChange={onChange} />
            </div>
            <Field label="Primary CTA link" path="hero.cta_primary.href" content={content} onChange={onChange} placeholder="/book" />
            <MediaField label="Hero background image (used when no carousel slides)" urlPath="hero.image_url" altPath="hero.image_alt" content={content} onChange={onChange} testid="hero-bg-media" />

            <div className="rounded-xl border border-[#E6EDF2] bg-[#F7FAFC] p-4 space-y-3" data-testid="hero-appearance">
              <p className="text-[14px] font-display font-bold text-[#1C4358]">Hero appearance</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <SizeField label="Hero logo size" path="hero.logo_size" content={content} onChange={onChange} customDefault={180} help="Default: Extra Large (larger logo)." />
                <SelectField label="Hero height" path="hero.height" content={content} onChange={onChange} options={HERO_HEIGHT_OPTS} fallback="standard" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <SelectField label="Text area top buffer" path="hero.pad_top" content={content} onChange={onChange} options={BUFFER_OPTS} fallback="normal" />
                <SelectField label="Text area bottom buffer" path="hero.pad_bottom" content={content} onChange={onChange} options={BUFFER_OPTS} fallback="normal" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <SelectField label="Background focal point — vertical" path="hero.focal_y" content={content} onChange={onChange} options={FOCAL_Y_OPTS} fallback="center" help="Move the background image up or down to fit." />
                <SelectField label="Background focal point — horizontal" path="hero.focal_x" content={content} onChange={onChange} options={FOCAL_X_OPTS} fallback="center" />
              </div>
            </div>

            <HeroSlidesEditor content={content} onChange={onChange} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="sections" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Page Sections — show / hide / reorder</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <SectionsEditor content={content} onChange={onChange} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="persona" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Persona Panel</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Title" path="persona.title" content={content} onChange={onChange} textarea rows={2} />
            <Field label="Subtitle" path="persona.subtitle" content={content} onChange={onChange} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="services" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Service Lanes</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Section title" path="services.title" content={content} onChange={onChange} />
            <Field label="Section subtitle" path="services.subtitle" content={content} onChange={onChange} />
            {(content.services?.items || []).map((it, i) => (
              <div key={it.key} className="rounded-lg border border-[#E6EDF2] p-3 space-y-3">
                <Field label={`Card ${i + 1} — Title`} path={`services.items.${i}.title`} content={content} onChange={onChange} />
                <Field label="Description" path={`services.items.${i}.description`} content={content} onChange={onChange} textarea rows={2} />
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="process" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">1-2-3 Process</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Section title" path="process.title" content={content} onChange={onChange} />
            <Field label="Section subtitle" path="process.subtitle" content={content} onChange={onChange} />
            {(content.process?.steps || []).map((st, i) => (
              <div key={i} className="rounded-lg border border-[#E6EDF2] p-3 space-y-3">
                <Field label={`Step ${i + 1} — Title`} path={`process.steps.${i}.title`} content={content} onChange={onChange} />
                <Field label="Description" path={`process.steps.${i}.description`} content={content} onChange={onChange} textarea rows={2} />
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="trust" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Trust Section</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Title" path="trust.title" content={content} onChange={onChange} textarea rows={2} />
            <RichField label="Body (rich text)" path="trust.body_html" content={content} onChange={onChange} />
            <MediaField label="Trust image" urlPath="trust.image_url" altPath="trust.image_alt" content={content} onChange={onChange} testid="trust-media" />
            {(content.trust?.points || []).map((p, i) => (
              <Field key={i} label={`Point ${i + 1}`} path={`trust.points.${i}`} content={content} onChange={onChange} />
            ))}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="trilogy" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Trilogy Wellness</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Title" path="trilogy.title" content={content} onChange={onChange} />
            <RichField label="Body (rich text)" path="trilogy.body_html" content={content} onChange={onChange} />
            <MediaField label="Trilogy image" urlPath="trilogy.image_url" altPath="trilogy.image_alt" content={content} onChange={onChange} testid="trilogy-media" />
            <Field label="CTA label" path="trilogy.cta.label" content={content} onChange={onChange} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="testimonials" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Testimonials</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Section title" path="testimonials.title" content={content} onChange={onChange} />
            <Field label="Section subtitle" path="testimonials.subtitle" content={content} onChange={onChange} />
            {(content.testimonials?.items || []).map((it, i) => (
              <div key={i} className="rounded-lg border border-[#E6EDF2] p-3 space-y-3">
                <Field label={`Quote ${i + 1}`} path={`testimonials.items.${i}.quote`} content={content} onChange={onChange} textarea rows={2} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Author" path={`testimonials.items.${i}.author`} content={content} onChange={onChange} />
                  <Field label="Location" path={`testimonials.items.${i}.location`} content={content} onChange={onChange} />
                </div>
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="resources" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Resources / Q&amp;A</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Section title" path="resources.title" content={content} onChange={onChange} />
            <Field label="Section subtitle" path="resources.subtitle" content={content} onChange={onChange} />
            {(content.resources?.items || []).map((it, i) => (
              <div key={i} className="rounded-lg border border-[#E6EDF2] p-3 space-y-3">
                <Field label={`Item ${i + 1} — Title`} path={`resources.items.${i}.title`} content={content} onChange={onChange} />
                <Field label="Excerpt" path={`resources.items.${i}.excerpt`} content={content} onChange={onChange} textarea rows={2} />
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="final" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Final CTA</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Title" path="final_cta.title" content={content} onChange={onChange} textarea rows={2} />
            <Field label="Subtitle" path="final_cta.subtitle" content={content} onChange={onChange} textarea />
            <Field label="Primary CTA label" path="final_cta.cta_primary.label" content={content} onChange={onChange} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="footer" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Footer</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Tagline" path="footer.tagline" content={content} onChange={onChange} textarea rows={2} />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Phone" path="footer.phone" content={content} onChange={onChange} />
              <Field label="Email" path="footer.email" content={content} onChange={onChange} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Address / Area" path="footer.address" content={content} onChange={onChange} />
              <Field label="Facebook URL" path="footer.facebook_url" content={content} onChange={onChange} placeholder="https://facebook.com/…" />
            </div>
            <SizeField label="Footer logo size" path="footer.logo_size" content={content} onChange={onChange} customDefault={150} help="Default: Extra Large. “MovingMyMama” stays directly beneath the logo." />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="welcome" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Welcome Video Pop-out</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <label className="flex items-center gap-2.5 text-[14px] font-semibold text-[#1C4358]">
              <Switch data-testid="editor-welcome-enabled" checked={!!getPath(content, "welcome_video.enabled")} onCheckedChange={(v) => onChange("welcome_video.enabled", v)} />
              Show a welcome video pop-out to first-time homepage visitors
            </label>
            <p className="text-[12px] text-[#6B7280]">Off by default. When on, the pop-out appears once per visitor (no autoplay with sound — the visitor clicks Play). A “{getPath(content, "welcome_video.button_label") || "Watch Welcome Video"}” button also appears in the Hero so anyone can replay it.</p>
            <Field label="Title" path="welcome_video.title" content={content} onChange={onChange} placeholder="Welcome to MovingMyMama" />
            <Field label="Short description" path="welcome_video.description" content={content} onChange={onChange} textarea rows={2} />
            <Field label="Video URL (YouTube, Vimeo, or .mp4)" path="welcome_video.video_url" content={content} onChange={onChange} placeholder="https://youtube.com/watch?v=…" />
            <MediaField label="Poster image (shown before play)" urlPath="welcome_video.poster_url" altPath="welcome_video.poster_alt" content={content} onChange={onChange} testid="welcome-poster-media" />
            <Field label="Replay button label" path="welcome_video.button_label" content={content} onChange={onChange} placeholder="Watch Welcome Video" />
            <div className="grid sm:grid-cols-2 gap-3">
              <SelectField label="When to show it" path="welcome_video.timing" content={content} onChange={onChange} options={VIDEO_TIMING_OPTS} fallback="delay" />
              <Field label="Delay in seconds (for “after a few seconds”)" path="welcome_video.delay_seconds" content={content} onChange={onChange} placeholder="5" />
            </div>
            <p className="text-[12px] text-[#6B7280]">Testing tip: the pop-out shows only once per visitor. To see it again, open the site in a new private/incognito window, or clear the browser’s site data (localStorage key <code className="px-1 rounded bg-[#1C4358]/8">mmm_welcome_seen</code>).</p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="videos" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">Video Library</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <label className="flex items-center gap-2.5 text-[14px] font-semibold text-[#1C4358]">
              <Switch data-testid="editor-video-library-enabled" checked={!!getPath(content, "video_library.enabled")} onCheckedChange={(v) => onChange("video_library.enabled", v)} />
              Show a “More videos” section on the public homepage
            </label>
            <p className="text-[12px] text-[#6B7280]">Hidden by default to keep the homepage clean. Manage videos below anytime — turn this on when you’re ready to feature them.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Section title" path="video_library.title" content={content} onChange={onChange} placeholder="More from MovingMyMama" />
              <Field label="Section subtitle" path="video_library.subtitle" content={content} onChange={onChange} />
            </div>
            <VideoLibraryEditor content={content} onChange={onChange} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="seo" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="text-[16px] font-display font-semibold text-[#1C4358]">SEO</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <Field label="Page title" path="seo.title" content={content} onChange={onChange} />
            <Field label="Meta description" path="seo.description" content={content} onChange={onChange} textarea rows={2} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
